import { createBrowserClient } from "@supabase/ssr"
import type { SupabaseClient } from "@supabase/supabase-js"
import { getRootDomain } from "@/lib/cookies/root-domain"

// Singleton pattern: Store the client instance to prevent re-creation on every render
// This is CRITICAL for preventing auth race conditions where new subscriptions
// fire INITIAL_SESSION and clear valid sessions
let browserClient: SupabaseClient | null = null

// Data-plane prefixes we route THROUGH goldcenter.am's own `/sb/*` proxy so the
// browser never has to reach `<ref>.supabase.co` directly. Some visitor networks
// (e.g. certain Armenian mobile carriers) block `*.supabase.co`, which left the
// storefront's product reads hanging forever (homepage rows / /bullion stuck on
// skeletons) even though goldcenter.am itself loaded fine. Rewriting these
// requests same-origin means one reachable host fixes everyone. See
// app/sb/[...path]/route.ts. Auth (`/auth/v1`) intentionally stays on the direct
// host — a logged-in visitor on such a network otherwise has their GoTrue
// session-refresh / OTP-verify calls hang, stalling the whole app before render.
// NOTE: this rewrites only the FETCH path — the client is still constructed with
// the direct SUPABASE_URL, so the auth storage key (`sb-<ref>-auth-token`) and
// cookies are UNCHANGED; no one is logged out. (OAuth's `authorize` step is a
// full-page redirect, not a fetch, so it still goes direct — OTP/email/refresh,
// the primary flows here, are all fetches and are covered.)
const SAME_ORIGIN_PREFIXES = ["/rest/v1/", "/functions/v1/", "/storage/v1/", "/auth/v1/"]

function makeSameOriginFetch(supabaseUrl: string): typeof fetch {
  return (input, init) => {
    // Only rewrite in the browser, and only Supabase data-plane URLs.
    if (typeof window !== "undefined") {
      try {
        const rawUrl =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.href
              : input.url
        if (rawUrl && rawUrl.startsWith(supabaseUrl)) {
          const rest = rawUrl.slice(supabaseUrl.length) // e.g. "/rest/v1/bullion_products?..."
          if (SAME_ORIGIN_PREFIXES.some((p) => rest.startsWith(p))) {
            const proxied = window.location.origin + "/sb" + rest
            // Preserve a Request object's method/headers/body when one was passed.
            if (typeof input !== "string" && !(input instanceof URL)) {
              return fetch(new Request(proxied, input), init)
            }
            return fetch(proxied, init)
          }
        }
      } catch {
        // fall through to the default fetch on any parsing hiccup
      }
    }
    return fetch(input as any, init)
  }
}

export function createClient() {
  // Return existing client if already created (singleton)
  if (browserClient) {
    return browserClient
  }
  // Trim environment variables to remove any whitespace/newlines
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL?.trim()
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY?.trim()

  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn("Supabase environment variables are not configured. Some features may not work.")
    // Return a mock client for development/preview environments
    return {
      auth: {
        getUser: () => Promise.resolve({ data: { user: null }, error: null }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      },
    } as any
  }

  // Create and store the client (singleton)
  browserClient = createBrowserClient(supabaseUrl, supabaseAnonKey, {
    // Route data-plane requests through goldcenter.am's same-origin `/sb/*`
    // proxy so blocked-`*.supabase.co` networks can still read the storefront.
    global: { fetch: makeSameOriginFetch(supabaseUrl) },
    auth: {
      // NOTE: We set autoRefreshToken: false here, but @supabase/ssr's
      // createBrowserClient OVERRIDES this to isBrowser() (true).
      // The actual disable happens below via stopAutoRefresh().
      autoRefreshToken: false,
      // Keep session detection enabled
      detectSessionInUrl: true,
      persistSession: true,
    },
    // Use default base64url encoding — ASCII-safe, prevents non-ASCII cookie corruption
    cookies: {
      getAll() {
        if (typeof document === 'undefined') return []
        const parsed = document.cookie.split('; ').filter(Boolean).map(row => {
          const eqIdx = row.indexOf('=')
          if (eqIdx === -1) return { name: row, value: '' }
          return { name: row.substring(0, eqIdx), value: row.substring(eqIdx + 1) }
        })
        return parsed
      },
      setAll(cookies: { name: string; value: string; options?: any }[]) {
        if (typeof document === 'undefined') return

        const domain = getRootDomain()
        for (const { name, value, options } of cookies) {
          const path = options?.path ?? '/'
          const maxAge = options?.maxAge
          const sameSite = options?.sameSite ?? 'lax'
          const secure = options?.secure ?? process.env.NODE_ENV === 'production'
          const cookieDomain = options?.domain ?? domain

          let cookieString = `${name}=${value}; path=${path}; SameSite=${sameSite}`
          if (maxAge !== undefined && maxAge !== null) cookieString += `; max-age=${maxAge}`
          if (secure) cookieString += '; Secure'
          if (cookieDomain) cookieString += `; domain=${cookieDomain}`

          document.cookie = cookieString
        }
      },
    },
  })

  // CRITICAL: @supabase/ssr's createBrowserClient overrides autoRefreshToken
  // to isBrowser() (true), ignoring our false setting. This causes Supabase's
  // built-in auto-refresh to run IN ADDITION to our custom Leader Tab refresh
  // in AuthContext. The two systems can collide and create a token refresh
  // storm (110ms infinite loops, 10,000+ refreshes/day per affected user).
  //
  // We CANNOT call stopAutoRefresh() synchronously here because
  // createBrowserClient triggers an async _initialize() → startAutoRefresh()
  // chain. Calling stopAutoRefresh() before that completes has no effect.
  // Instead, we stop it after the first INITIAL_SESSION event fires
  // (which signals _initialize() has completed). See AuthContext.
  //
  // As a safety net, also stop after a delay to catch edge cases where
  // AuthContext hasn't mounted yet.
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      browserClient?.auth.stopAutoRefresh()
    }, 3000)
  }

  return browserClient
}
